import { useVendorActions } from '@melio/ap-domain';
import { NakedButton, Table } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { Bill, Vendor } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useIsFeatureInSubscription, useSubscriptionContextualPaywall } from '@melio/subscriptions-sdk';
import { useLocation } from 'react-router-dom';

import { useActivitiesNavigate } from '../../../../../utils';

export type VendorsTabActionButtonPropsType = {
  vendor: Vendor;
  unpaidBills: Bill[];
  isDisabled?: boolean;
  newPaymentAmount?: string;
  onNewPaymentClick: (vendorId: string, amount?: string) => void;
  onViewVendorClick: (vendorId: string) => void;
};

export const VendorsTabActionButtonCell = ({
  vendor,
  unpaidBills,
  isDisabled,
  newPaymentAmount,
  onNewPaymentClick,
  onViewVendorClick,
}: VendorsTabActionButtonPropsType) => {
  const { formatMessage, formatCurrency } = useMelioIntl();
  const { track } = useAnalytics();
  const vendorActions = useVendorActions(vendor);
  const { navigateToBatchPayment, navigateToSchedulePayment } = useActivitiesNavigate();
  const { pathname } = useLocation();

  const { isInSubscription: isBatchPaymentsInSubscription } = useIsFeatureInSubscription('batchPayments');
  const { showPaywall } = useSubscriptionContextualPaywall();

  const openBalance = unpaidBills.reduce((acc, bill) => acc + bill.balance, 0);
  const noUnpaidBills = unpaidBills.length === 0;

  const handleNewPaymentClick = () => {
    track('Dashboard', 'Click', {
      Intent: 'pay-vendor',
      Cta: 'new-payment',
      VendorId: vendor.id,
    });
    if (Number(newPaymentAmount)) {
      onNewPaymentClick(vendor.id, newPaymentAmount);
    } else {
      onNewPaymentClick(vendor.id);
    }
  };

  const onPayBills = (unpaidBillsIds: string[]) => {
    if (unpaidBills.length === 1 && unpaidBillsIds[0] /* for ts check */) {
      return navigateToSchedulePayment({ billId: unpaidBillsIds[0], returnUrl: pathname });
    }

    if (isBatchPaymentsInSubscription) {
      return navigateToBatchPayment({ billIds: unpaidBillsIds, returnUrl: pathname });
    }

    showPaywall({ featureName: 'batchPayments' });
  };

  const handlePayBalanceClick = () => {
    const unpaidBillIds = unpaidBills.map((bill) => bill.id);

    track('Dashboard', 'Click', {
      Intent: 'pay-vendor',
      Cta: 'pay-balance',
      BillIds: unpaidBillIds,
      VendorId: vendor.id,
      OpenBalance: openBalance,
    });

    onPayBills(unpaidBillIds);
  };

  const handleViewVendorClick = () => {
    track('Dashboard', 'Click', { Cta: 'view-details', Intent: 'view-vendor', VendorId: vendor.id });
    onViewVendorClick(vendor.id);
  };

  if (vendorActions.newPayment && !noUnpaidBills) {
    return (
      <ActionButton
        vendor={vendor}
        action={handlePayBalanceClick}
        label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.payBalance')}
        isDisabled={isDisabled}
        aria-label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.payBalance.link.ariaLabel', {
          vendorName: vendor.name,
          amount: formatCurrency(openBalance),
        })}
      />
    );
  }

  if (vendorActions.newBill) {
    return (
      <ActionButton
        vendor={vendor}
        action={handleNewPaymentClick}
        label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.newPayment')}
        isDisabled={isDisabled}
        aria-label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.newPayment.link.ariaLabel', {
          vendorName: vendor.name,
        })}
      />
    );
  }

  return (
    <ActionButton
      vendor={vendor}
      action={handleViewVendorClick}
      label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.viewDetails')}
      isDisabled={isDisabled}
      aria-label={formatMessage('activities.payDashboard.vendorsTab.table.cells.actions.viewDetails.link.ariaLabel')}
    />
  );
};

const ActionButton = ({
  vendor,
  action,
  label,
  'aria-label': ariaLabel,
  isDisabled,
}: {
  vendor: Vendor;
  action: () => void;
  label: string;
  'aria-label': string;
  isDisabled?: boolean;
}) => (
  <Table.Cell onClick={action} textAlign="end" data-tourid={`vendors-tab-action-cell-${vendor.id}`}>
    <NakedButton
      data-testid={`vendors-tab-action-cell-${vendor.id}`}
      variant="secondary"
      label={label}
      aria-label={ariaLabel}
      isDisabled={isDisabled}
    />
  </Table.Cell>
);
